import {
  createEnumOptions,
  IBaseFlowStepData,
  IOrder,
  ISelectionItemData,
  OrderStatus,
  SelectionItem,
} from '@kinet-open-network/shared/data-access/model';
import { IAppStockItem } from '../stock/app-stock-item.interface';
import { IAppOrderItem } from './app-order-item.interface';

export enum FlowOrderActions {
  changeStatus = 'changeStatus',
}

export const flowOrderActionsOptions = createEnumOptions<FlowOrderActions>({
  [FlowOrderActions.changeStatus]: {
    label: 'Zmeniť stav',
    icon: 'pi pi-check',
  },
  _default: { label: '-' },
});

export enum FlowOrderType {
  order = 'order',
  collection = 'collection',
  storeSale = 'storeSale',
}

export const flowOrderTypeOptions = createEnumOptions<FlowOrderType>({
  [FlowOrderType.order]: {
    label: 'Objednávka',
    icon: 'pi pi-shopping-cart',
    color: 'primary',
  },
  [FlowOrderType.collection]: {
    label: 'Zvoz',
    icon: 'pi pi-truck',
    color: 'info',
  },
  [FlowOrderType.storeSale]: {
    label: 'Predaj na predajni',
    icon: 'pi pi-shop',
    color: 'none',
  },
  _default: { label: '-' },
});

/** Statuses that can be set on an order, keyed by the order's `type`. */
export const availableStatusesForType: Record<FlowOrderType, OrderStatus[]> = {
  [FlowOrderType.order]: Object.values(OrderStatus),
  [FlowOrderType.collection]: [
    OrderStatus.draft,
    OrderStatus.approved,
    OrderStatus.delivered,
    OrderStatus.collection,
    OrderStatus.finished,
  ],
  [FlowOrderType.storeSale]: [
    OrderStatus.draft,
    OrderStatus.approved,
    OrderStatus.finished,
  ],
};

export interface IFlowOrder extends IOrder<'Order'> {
  items: IAppOrderItem[];

  status: OrderStatus;

  type: FlowOrderType;

  toCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  toCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  fromCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  fromCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];

  unloadingStockCode?: string;

  /** Invoice generated for this order — preserved across invoice regeneration. */
  invoiceId?: string | null;

  recipientName?: string;

  recipientEmail?: string;

  recipientSignature?: string;

  /** Delivery location (longitude) — filled by the ca-gateway API. */
  recipientGpsX?: number | null;

  /** Delivery location (latitude) — filled by the ca-gateway API. */
  recipientGpsY?: number | null;

  /** Date and time of the delivery / collection / sale. */
  deliveryDate?: Date | string | null;

  dueDays?: number | null;

  generateInvoice?: boolean;
}

export interface IFlowOrderStepData extends IBaseFlowStepData {
  deliveryId?: string;

  toCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  toCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  fromCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  fromCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];

  unloadingStockCode?: string;

  recipientName?: string;

  recipientEmail?: string;

  recipientSignature?: string;

  /** Date and time of the delivery / collection / sale. */
  deliveryDate?: Date | string | null;

  /**
   * When true, just persists the selection items without advancing status or moving stock.
   * Defaults to false (proceed to next status).
   */
  autoSaveItems?: boolean;

  /**
   * Status of the step being submitted (see `flowOrderStatusSteps`) — sent
   * both when the step is performed and when it is edited. Defaults to the
   * order's current status.
   */
  status?: OrderStatus;

  /**
   * Retroactively edits an already completed step (identified by `status`)
   * instead of performing it: the order status is left untouched.
   */
  isEditStep?: boolean;

  /** With `isEditStep`: re-sync the stock moves of the edited step. */
  regenerateStockDocuments?: boolean;

  /** With `isEditStep`: re-generate the order's invoice from the edited items. */
  regenerateInvoice?: boolean;
}

export interface IFlowOrderSelectionItemData extends ISelectionItemData {
  isFull?: boolean;
  isSell?: boolean;
  isComplaint?: boolean;
  stockItemId: string;
}

export interface IFlowOrderScannedItem {
  id: string;
  sku?: string;
  sn: string;
}

export function stockItemToFlowOrderScannedItem(
  stockItem: IAppStockItem & { sn: string },
): IFlowOrderScannedItem {
  return {
    id: stockItem.id,
    sku: stockItem.sku ?? undefined,
    sn: stockItem.sn,
  };
}

export const orderGuidExt = (orderId: number | string) =>
  `crm-order-${orderId}`;

/**
 * Extracts the CRM order id from an Onix `Guid_Ext` value produced by
 * {@link orderGuidExt}. Returns `null` when the value does not match.
 */
export const parseOrderGuidExt = (guidExt?: string | null): string | null => {
  const match = /crm-order-(\d+)/.exec(guidExt ?? '');
  return match ? match[1] : null;
};
